import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { OptionGroupsEditor, AddonsEditor } from "@/components/admin/OptionGroupsEditor";

const EMPTY = { name: "", description: "", category: "", price: "", compare_price: "", stock: "", image_url: "", active: true, option_groups: [], addons: [] };

export const ProductDialog = ({ open, onOpenChange, product, categories, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => { if (open) setForm(product ? { ...EMPTY, ...product } : EMPTY); }, [open, product]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form, price: Number(form.price), stock: parseInt(form.stock, 10),
        compare_price: form.compare_price ? Number(form.compare_price) : null };
      delete body.id; delete body.created_at;
      if (product) await api.put(`/admin/products/${product.id}`, body);
      else await api.post("/admin/products", body);
      toast.success(product ? "Produto atualizado" : "Produto cadastrado");
      onSaved();
      onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="product-dialog" className="max-h-[90vh] overflow-y-auto bg-[#1A1A1A]">
        <DialogHeader><DialogTitle className="font-display text-2xl">{product ? "Editar produto" : "Novo produto"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Nome</Label><Input required data-testid="product-name-input" value={form.name} onChange={set("name")} /></div>
          <div><Label>Categoria</Label><Input required list="cats" data-testid="product-category-input" value={form.category} onChange={set("category")} />
            <datalist id="cats">{categories.map((c) => <option key={c} value={c} />)}</datalist></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Preço (R$)</Label><Input required type="number" min={0} step="0.01" data-testid="product-price-input" value={form.price} onChange={set("price")} /></div>
            <div><Label>Estoque</Label><Input required type="number" min={0} step="1" data-testid="product-stock-input" value={form.stock} onChange={set("stock")} /></div>
          </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Preço de comparação (R$)</Label><Input type="number" min={0} step="0.01" data-testid="product-compare-price-input" placeholder="opcional" value={form.compare_price ?? ""} onChange={set("compare_price")} />
                <p className="mt-1 text-xs text-slate-500">Mostra o preço riscado e o selo "Economize"</p></div>
            </div>
          <ImageUpload value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))} />
          <div><Label>Descrição</Label><Input data-testid="product-description-input" value={form.description} onChange={set("description")} /></div>
          <OptionGroupsEditor groups={form.option_groups || []} onChange={(groups) => setForm((f) => ({ ...f, option_groups: groups }))} />
          <AddonsEditor addons={form.addons || []} onChange={(addons) => setForm((f) => ({ ...f, addons }))} />
          <div className="flex items-center gap-3"><Switch data-testid="product-active-switch" checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} /><Label>Visível na loja</Label></div>
          <Button data-testid="product-save-button" disabled={saving} className="w-full rounded-full font-bold">{saving ? "Salvando..." : "Salvar"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
